import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import "regenerator-runtime";

// import TuneIcon from "@mui/icons-material/Tune";
import { Stack, TextField } from "@mui/material";
// import InputAdornment from "@mui/material/InputAdornment";

import { useTranslation } from "@/components/i18n/Translator";
// import HistoryButton from "@/components/organisms/chat/history";

import { IAttachment, attachmentsState } from "@/state/chat";
// import { chatSettingsOpenState } from "@/state/project";
import { inputHistoryState } from "@/state/userInputHistory";

// import MicButton from "./MicButton";
import { SubmitButton } from "./SubmitButton";
// import UploadButton from "./UploadButton";
import { FileSpec } from "@/client/types";
import { useChatData } from "@/client/useChatData";
// import QnAButton from "./QnaButton";
// import UpdateReportButton from "./UpdateReportButton";

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  onSubmit: (message: string, attachments?: IAttachment[]) => void;
  onReply: (message: string) => void;
  page: "session" | "report"; // New page prop
}

function getLineCount(el: HTMLDivElement) {
  const textarea = el.querySelector("textarea");
  if (!textarea) {
    return 0;
  }
  const lines = textarea.value.split("\n");
  return lines.length;
}

const Input = memo(
  ({
    // fileSpec,
    onFileUpload,
    // onFileUploadError,
    onSubmit,
    onReply,
    page,
  }: Props) => {
    const [attachments, setAttachments] = useRecoilState(attachmentsState);
    const setInputHistory = useSetRecoilState(inputHistoryState);

    // const setChatSettingsOpen = useSetRecoilState(chatSettingsOpenState);

    const ref = useRef<HTMLDivElement>(null);
    const {
      loading,
      askUser,
      // chatSettingsInputs,
      disabled: _disabled,
    } = useChatData();

    const disabled = _disabled || !!attachments.find((a) => !a.uploaded);

    const [value, setValue] = useState("");
    const [isComposing, setIsComposing] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
      const pasteEvent = (event: ClipboardEvent) => {
        if (event.clipboardData && event.clipboardData.items) {
          const items = Array.from(event.clipboardData.items);

          const textData = event.clipboardData.getData("text/plain");
          if (textData) return;

          items.forEach((item) => {
            if (item.kind === "file") {
              const file = item.getAsFile();
              if (file) {
                onFileUpload([file]);
              }
            }
          });
        }
      };

      if (!ref.current) return;

      const input = ref.current;
      input.addEventListener("paste", pasteEvent);
      return () => input.removeEventListener("paste", pasteEvent);
    }, []);

    useEffect(() => {
      if (ref.current && !loading && !disabled) {
        ref.current.focus();
      }
    }, [loading, disabled]);

    const submit = useCallback(() => {
      if (value === "" || disabled) return;
      if (askUser) {
        onReply(value);
      } else {
        onSubmit(value, attachments);
      }
      setAttachments([]);
      setValue("");
    }, [value, disabled, askUser, attachments]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
          if (!isComposing) {
            e.preventDefault();
            submit();
          }
        } else if (e.key === "ArrowUp") {
          const lineCount = getLineCount(e.currentTarget as HTMLDivElement);
          if (lineCount <= 1) {
            setInputHistory((old) => ({ ...old, open: true }));
          }
        }
      },
      [submit, isComposing]
    );

    // const onHistoryClick = useCallback((content: string) => {
    //   if (ref.current) setValue(content);
    // }, []);

    return (
      <Stack
        gap={1}
        direction="row"
        alignItems="flex-end"
        sx={{
          width: "100%",
          backgroundColor: "transparent",
          borderRadius: "1.5rem",
          border: (theme) =>
            theme.palette.mode === "dark" ? "none" : "1px solid #CBD5E1",
          px: 2,
          py: 1.5,
          boxShadow: "0px 2px 4px 0px #0000000D",
          flexWrap: "wrap",
        }}
      >
        {/* Input Field */}
        <TextField
          inputRef={ref}
          id="chat-input"
          multiline
          minRows={1}
          maxRows={10}
          variant="standard"
          fullWidth
          placeholder={
            page === "session"
              ? t("components.organisms.chat.inputBox.input.placeholder")
              : t("components.organisms.chat.inputBox.report.placeholder")
          }
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          value={value}
          InputProps={{
            disableUnderline: true,
            sx: {
              backgroundColor: "transparent",
              padding: "0.7rem",
              borderRadius: "15px",
              color: "text.primary",
            },
          }}
        />

        {/* Bottom adornment */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          width="100%"
          flexWrap="wrap"
        >
          {/* Left Buttons */}
          <Stack direction="row" spacing={1} alignItems="flex-end">
            {/* <QnAButton />
            <UpdateReportButton />
            <UploadButton
              disabled={disabled}
              fileSpec={fileSpec}
              onFileUploadError={onFileUploadError}
              onFileUpload={onFileUpload}
            /> */}
          </Stack>

          {/* Right Button */}
          <SubmitButton onSubmit={submit} disabled={disabled || !value} />
        </Stack>
      </Stack>
    );
  }
);

export default Input;
